// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { svgHistogram, type HistBin } from '../components/charts';
import { openQuakeDrawer } from '../components/detail';
import { infoI } from '../components/overlays';
import { depthKm, esc, mag as fmtMag, num, relTime } from '../format';
import { magColor } from '../model';
import { quakes, regionOf } from '../state';
import type { Quake } from '../types';
import { emptyState } from './shared';

type SortKey = 'time' | 'mag' | 'depth' | 'felt' | 'place';
const ROW_CAP = 500;

const s = {
  search: '',
  sort: 'time' as SortKey,
  dir: -1 as 1 | -1,
  magFloor: null as number | null, // selected 1-mag band lower edge
};
let debounce: ReturnType<typeof setTimeout> | null = null;

export function renderExplorer(root: HTMLElement): void {
  const all = quakes();
  if (all.length === 0) { root.innerHTML = emptyState(); return; }

  // histogram over ALL current events (by 1.0 mag bin)
  const lo = Math.floor(Math.min(...all.map((q) => q.mag)));
  const hi = Math.ceil(Math.max(...all.map((q) => q.mag)));
  const bandCounts: number[] = [];
  for (let m = lo; m < hi; m++) bandCounts.push(all.filter((q) => q.mag >= m && q.mag < m + 1).length);
  const histBins: HistBin[] = bandCounts.map((c, i) => {
    const m = lo + i;
    return {
      label: `${m}`,
      count: c,
      color: magColor(m + 0.5),
      tip: `M ${m}–${m + 1}: ${c} events — click to filter`,
      id: String(m),
      active: s.magFloor === m,
    };
  });

  const filtered = applyFilters(all);
  const sorted = sortRows(filtered);
  const shown = sorted.slice(0, ROW_CAP);

  root.innerHTML = `
    <div class="panel">
      <div class="panel-title">Explore every event ${infoI('significance')}</div>
      <div class="panel-sub">Search, sort and drill into all ${num(all.length)} earthquakes in the current window. Click a magnitude bar to filter; click any row for full detail.</div>
      <div class="chart-wrap">${svgHistogram(histBins, { height: 150 })}</div>
      <div class="filter-bar" style="margin-top:0.75rem">
        <input class="search-input" id="q-search" type="search" placeholder="Search place or region…" value="${esc(s.search)}" aria-label="Search">
        ${s.magFloor !== null ? `<span class="pill-btn active">M ${s.magFloor}–${s.magFloor + 1}</span>` : ''}
        ${s.search || s.magFloor !== null ? '<button class="clear-btn" id="q-clear">Clear filters</button>' : ''}
        <span class="muted">${num(filtered.length)} match${filtered.length === 1 ? '' : 'es'}</span>
      </div>
      <div class="table-wrap">
        <table class="data">
          <thead><tr>
            ${th('time', 'Time')}${th('mag', 'Mag')}${th('depth', 'Depth')}${th('place', 'Location')}
            <th>Region</th>${th('felt', 'Felt')}<th>Flags</th>
          </tr></thead>
          <tbody>${shown.map(rowHtml).join('')}</tbody>
        </table>
      </div>
      ${sorted.length > ROW_CAP ? `<div class="muted" style="margin-top:0.5rem">Showing the first ${ROW_CAP} of ${num(sorted.length)} matches — refine your search to see the rest.</div>` : ''}
    </div>`;

  const search = root.querySelector('#q-search') as HTMLInputElement;
  search?.addEventListener('input', () => {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => { s.search = search.value; renderExplorer(root); const el = root.querySelector('#q-search') as HTMLInputElement; el?.focus(); el?.setSelectionRange(el.value.length, el.value.length); }, 300);
  });
  root.querySelector('#q-clear')?.addEventListener('click', () => { s.search = ''; s.magFloor = null; renderExplorer(root); });
  root.querySelectorAll('.hist-bar[data-id]').forEach((b) => b.addEventListener('click', () => {
    const m = Number(b.getAttribute('data-id'));
    s.magFloor = s.magFloor === m ? null : m;
    renderExplorer(root);
  }));
  root.querySelectorAll('thead th[data-sort]').forEach((h) => h.addEventListener('click', () => {
    const key = h.getAttribute('data-sort') as SortKey;
    if (s.sort === key) s.dir = (s.dir * -1) as 1 | -1; else { s.sort = key; s.dir = key === 'place' ? 1 : -1; }
    renderExplorer(root);
  }));
  root.querySelectorAll('tbody tr[data-id]').forEach((tr) => tr.addEventListener('click', () => {
    const q = all.find((x) => x.id === tr.getAttribute('data-id'));
    if (q) openQuakeDrawer(q);
  }));
}

function applyFilters(all: Quake[]): Quake[] {
  const term = s.search.trim().toLowerCase();
  return all.filter((q) => {
    if (s.magFloor !== null && !(q.mag >= s.magFloor && q.mag < s.magFloor + 1)) return false;
    if (term && !(q.place.toLowerCase().includes(term) || regionOf(q).toLowerCase().includes(term))) return false;
    return true;
  });
}

function sortRows(rows: Quake[]): Quake[] {
  const key = s.sort;
  return [...rows].sort((a, b) => {
    let av: number | string;
    let bv: number | string;
    if (key === 'place') { av = a.place; bv = b.place; return s.dir * av.localeCompare(bv); }
    av = a[key]; bv = b[key];
    return s.dir * ((av as number) - (bv as number));
  });
}

function th(key: SortKey, label: string): string {
  const arrow = s.sort === key ? (s.dir === -1 ? ' ▾' : ' ▴') : '';
  return `<th data-sort="${key}">${label}${arrow}</th>`;
}

function rowHtml(q: Quake): string {
  const flags = [
    q.tsunami ? '<span class="chip tsunami">tsunami</span>' : '',
    q.alert ? `<span class="chip alert-${esc(q.alert)}">${esc(q.alert)}</span>` : '',
    q.type !== 'earthquake' ? `<span class="chip blast">${esc(q.type)}</span>` : '',
  ].filter(Boolean).join(' ');
  return `<tr data-id="${esc(q.id)}">
    <td class="mono">${relTime(q.time)}</td>
    <td><span class="mag-pill" style="background:${magColor(q.mag)}">${fmtMag(q.mag)}</span></td>
    <td class="mono">${depthKm(q.depth)}</td>
    <td>${esc(q.place)}</td>
    <td class="muted">${esc(regionOf(q))}</td>
    <td class="mono">${q.felt ? num(q.felt) : '—'}</td>
    <td>${flags || '<span class="muted">—</span>'}</td>
  </tr>`;
}
